import type { CrmRecordTypeFieldName, CrmRecordTypeFields } from "@/lib/api/crm/masters";
import { marketingFeeToDisplayPercent } from "@/lib/customer";
import { CommonDestinationTimezone } from "@/lib/api/utils";
import { safeParseDateMomentWith } from "@/lib/api/date";

export interface RecordFieldGroup {
  title: string;
  fields: CrmRecordTypeFieldName[];
}

/**
 * Display groups for type-specific fields, in the groups @lhc/crm records/record-types.ts defines.
 * Which fields a record type has comes from GET /crm/rules; see `fieldGroupsFor`.
 * Notes are shown separately at the top of a record.
 */
const shippingAndHoursGroup: RecordFieldGroup = {
  title: "Shipping & hours",
  fields: ["shipping_instructions", "regular_hours", "current_week_hours", "next_week_hours"],
};

const accountGroup: RecordFieldGroup = {
  title: "Account",
  // HarvestIQ profile and "Always show activities" aren't shown; skip_harvest_iq_sync shows as "Sync to risk".
  fields: [
    "trader_id",
    "marketing_fee",
    "active_account_codes",
    "skip_harvest_iq_sync",
    "preferred_defer_payment_type",
    "deferred_payment_lien_status",
    "signed_up_date",
    "partner_names",
    "shirt_sizes",
    "original_acres",
    "bol_sequence",
  ],
};

const detailsGroup: RecordFieldGroup = {
  title: "Details",
  fields: ["is_commercial"],
};

/** Group order per layout: the detail page shows shipping & hours below the account fields. */
const recordFieldGroups: Record<"detail" | "form", RecordFieldGroup[]> = {
  detail: [accountGroup, shippingAndHoursGroup, detailsGroup],
  form: [shippingAndHoursGroup, accountGroup, detailsGroup],
};

export const recordFieldLabels: Record<CrmRecordTypeFieldName, string> = {
  notes: "Notes",
  shipping_instructions: "Shipping instructions",
  regular_hours: "Regular hours",
  current_week_hours: "This week's hours",
  next_week_hours: "Next week's hours",
  marketing_fee: "Marketing fee",
  active_account_codes: "Active account codes",
  harvest_iq_profile: "HarvestIQ profile",
  // Shown as `isSyncedToRisk` (see `recordFieldValue`).
  skip_harvest_iq_sync: "Sync to risk",
  force_show_activities: "Always show activities",
  preferred_defer_payment_type: "Preferred deferred payment",
  deferred_payment_lien_status: "Deferred payment lien",
  signed_up_date: "Signed up",
  partner_names: "Partners",
  shirt_sizes: "Shirt sizes",
  original_acres: "Original acres",
  bol_sequence: "BOL sequence",
  is_commercial: "Commercial",
  // Shown as the trader's name (see `TraderName`).
  trader_id: "Trader",
};

const textFormatters: Partial<Record<CrmRecordTypeFieldName, (value: string) => string>> = {
  // Stored as a fraction (0.015), shown as a percentage (1.5%).
  marketing_fee: (value) => {
    const percent = marketingFeeToDisplayPercent(value);
    return percent ? `${percent}%` : value;
  },
  preferred_defer_payment_type: (value) => (value === "ach" ? "ACH" : value === "check" ? "Check" : value),
  deferred_payment_lien_status: (value) => (value === "active" ? "Active" : value),
  signed_up_date: (value) =>
    safeParseDateMomentWith(value, "YYYY-MM-DD", CommonDestinationTimezone)?.format("MMM D, YYYY") ?? value,
};

/** The groups containing the given fields (a record type's fields from GET /crm/rules), in the layout's order. */
export function fieldGroupsFor<F extends CrmRecordTypeFieldName>(
  fields: F[],
  layout: "detail" | "form",
): { title: string; fields: F[] }[] {
  const included = new Set<CrmRecordTypeFieldName>(fields);
  return recordFieldGroups[layout]
    .map((group) => ({ ...group, fields: group.fields.filter((field): field is F => included.has(field)) }))
    .filter((group) => group.fields.length > 0);
}

export function formatRecordFieldText(field: CrmRecordTypeFieldName, value: string): string {
  return textFormatters[field]?.(value) ?? value;
}

/** Synced to risk only when HarvestIQ sync isn't skipped and there is a HarvestIQ profile. */
export function isSyncedToRisk(record: CrmRecordTypeFields): boolean {
  return record.skip_harvest_iq_sync === false && Boolean(record.harvest_iq_profile);
}

/** The value shown for a field: skip_harvest_iq_sync shows as "Sync to risk" (`isSyncedToRisk`). */
export function recordFieldValue(
  field: CrmRecordTypeFieldName,
  record: CrmRecordTypeFields,
): CrmRecordTypeFields[CrmRecordTypeFieldName] {
  return field === "skip_harvest_iq_sync" ? isSyncedToRisk(record) : record[field];
}
